import React, { useState, useEffect } from 'react';
import Layout from '../../Components/Layout';
import { useParams, useNavigate } from 'react-router-dom';
import tourService from '../../services/tourService';
import { FaStar, FaMapMarkerAlt, FaClock, FaUsers, FaCalendarAlt, FaPlus, FaMinus, FaCheck, FaShoppingCart, FaCalendarCheck, FaTrash } from 'react-icons/fa';
import Swal from 'sweetalert2';
import OwlCarousel from 'react-owl-carousel';
import config from '../../config';
import axios from 'axios';

export default function TourDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [tour, setTour] = useState(null);
    const [error, setError] = useState(null);
    const [priceData, setPriceData] = useState(null);
    const [priceLoading, setPriceLoading] = useState(false);
    
    // Form states
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [adults, setAdults] = useState(1);
    const [children, setChildren] = useState([]);
    const [selectedDateRange, setSelectedDateRange] = useState('');
    const [activeTab, setActiveTab] = useState('about');
    
    // Date range filter states
    const [dateRangeStart, setDateRangeStart] = useState('');
    const [dateRangeEnd, setDateRangeEnd] = useState('');
    const [filteredAvailableDates, setFilteredAvailableDates] = useState([]);
    
    // Cart states
    const [cartSummary, setCartSummary] = useState({
        totalItems: 0,
        items: [],
        totalPrice: 0
    });

    // API'den tour detayını çek
    useEffect(() => {
        const fetchTourDetail = async () => {
            try {
                setLoading(true);
                setError(null);
                
                const response = await tourService.getTourDetail(id);
                
                if (response && response.data) {
                    setTour(response.data);
                    
                    // Default tarih ayarla (bugünden itibaren 5 gün)
                    const today = new Date();
                    const start = new Date(today);
                    start.setDate(today.getDate() + 1);
                    const end = new Date(start);
                    end.setDate(start.getDate() + 4);
                    
                    setStartDate(start.toISOString().split('T')[0]);
                    setEndDate(end.toISOString().split('T')[0]);
                    
                    // Default date range filter ayarla (bugünden itibaren 30 gün)
                    const filterEnd = new Date(today);
                    filterEnd.setDate(today.getDate() + 30);
                    
                    setDateRangeStart(start.toISOString().split('T')[0]);
                    setDateRangeEnd(filterEnd.toISOString().split('T')[0]);
                    
                    // Available dates'i filtrele
                    if (response.data.availableDates) {
                        setFilteredAvailableDates(response.data.availableDates);
                    }
                } else {
                    setError('Tour details not found');
                }
            } catch (err) {
                console.error('Error fetching tour detail:', err);
                setError(err.message || 'Failed to load tour details');
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchTourDetail();
        } else {
            navigate('/tours');
        }
    }, [id, navigate]);

    // Price calculator API'sini çağır
    const calculatePrice = async (dateRange = null) => {
        const rangesToUse = dateRange || selectedDateRange;
        if (!rangesToUse || !tour) return;
        
        try {
            setPriceLoading(true);
            
            // Date range'i parse et (örn: "2025-07-07 - 2025-07-08")
            const [startDateStr, endDateStr] = rangesToUse.split(' - ');
            
            const requestData = {
                start_date: startDateStr,
                end_date: endDateStr,
                adults: adults,
                children: children.map(child => ({ age: child.age }))
            };
            
            const response = await tourService.calculateTourPrice(id, requestData);
            
            if (response && response.success) {
                setPriceData(response);
            }
        } catch (err) {
            console.error('Error calculating price:', err);
        } finally {
            setPriceLoading(false);
        }
    };

    // Available date seçildiğinde price calculator çağır ve Book Your Tour bölümüne scroll et
    const handleDateSelection = (dateRange) => {
        setSelectedDateRange(dateRange);
        calculatePrice(dateRange);
        
        // Book Your Tour bölümüne scroll et
        setTimeout(() => {
            const bookingSection = document.querySelector('.modern-booking-card');
            if (bookingSection) {
                bookingSection.scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'start' 
                });
            }
        }, 100); // Kısa bir gecikme ile DOM güncellemesini bekle
    };

    // Form değiştiğinde price calculator çağır (sadece adults ve children için)
    useEffect(() => {
        if (tour && selectedDateRange) {
            calculatePrice();
        }
    }, [adults, children]);

    // Children yaş ekleme/çıkarma
    const addChild = () => {
        setChildren([...children, { age: 5 }]);
    };

    const removeChild = (index) => {
        const newChildren = children.filter((_, i) => i !== index);
        setChildren(newChildren);
    };

    const updateChildAge = (index, age) => {
        const newChildren = [...children];
        newChildren[index] = { age: parseInt(age) };
        setChildren(newChildren);
    };

    // Tarih formatı
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    // Tarih formatı (hem tek tarih hem de aralık için: "2025-08-09" → "12 May 2025" veya "2025-07-07 - 2025-07-08" → "12 May 2025 - 13 May 2025")
    const formatDateRange = (dateRange) => {
        if (!dateRange) return dateRange;
        
        // Tek tarih kontrolü (aralık değilse)
        if (!dateRange.includes(' - ')) {
            const singleDate = new Date(dateRange);
            return singleDate.toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            });
        }
        
        // Tarih aralığı formatı
        const [startDate, endDate] = dateRange.split(' - ');
        const start = new Date(startDate);
        const end = new Date(endDate);
        
        const startFormatted = start.toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
        
        const endFormatted = end.toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
        
        return `${startFormatted} - ${endFormatted}`;
    };

    // Available dates'i filter fonksiyonu
    const filterAvailableDates = () => {
        if (!tour?.availableDates || !dateRangeStart || !dateRangeEnd) {
            setFilteredAvailableDates(tour?.availableDates || []);
            return;
        }

        const startFilter = new Date(dateRangeStart);
        const endFilter = new Date(dateRangeEnd);

        const filtered = tour.availableDates.filter(dateOption => {
            // Date format: "2025-07-07 - 2025-07-08"
            const [dateStart] = dateOption.date.split(' - ');
            const tourDate = new Date(dateStart);
            
            return tourDate >= startFilter && tourDate <= endFilter;
        });

        setFilteredAvailableDates(filtered);
    };

    // Cart functions
    const addToCart = () => {
        if (!selectedDateRange || !priceData?.quick_summary) {
            Swal.fire({
                title: 'Incomplete Selection',
                text: 'Please select dates and ensure price is calculated',
                icon: 'warning',
                confirmButtonText: 'OK'
            });
            return;
        }

        const cartItem = {
            id: `${tour.id}-${selectedDateRange}-${adults}-${children.length}`,
            tourId: tour.id,
            tourName: tour.name,
            tourImage: tour.images?.[0]?.path || '/assets/img/tours/default-tour.jpg',
            dateRange: selectedDateRange,
            adults: adults,
            children: children,
            totalPrice: priceData.quick_summary.grand_total,
            currency: tour?.currency?.symbol || '€',
            perPersonAvg: priceData.quick_summary.per_person_avg,
            offerKey: priceData.offerKey, // API'den gelen offerKey'i ekle
            quantity: 1
        };

        // Get existing cart from localStorage
        const existingCart = JSON.parse(localStorage.getItem('tour_cart')) || { data: [] };
        
        // Check if item already exists
        const existingItemIndex = existingCart.data.findIndex(item => item.id === cartItem.id);
        
        if (existingItemIndex > -1) {
            Swal.fire({
                title: 'Item Already in Cart',
                text: 'This tour with the same configuration is already in your cart',
                icon: 'info',
                confirmButtonText: 'OK'
            });
            return;
        }

        // Add new item to cart
        existingCart.data.push(cartItem);
        
        // Save to localStorage
        localStorage.setItem('tour_cart', JSON.stringify(existingCart));
        
        // Update cart summary
        updateCartSummary();
        
        // Show success message
        Swal.fire({
            toast: true,
            position: 'top-right',
            title: 'Added to Cart!',
            text: `${tour.name} has been added to your cart`,
            icon: 'success',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true
        });
    };

    const updateCartSummary = () => {
        const cart = JSON.parse(localStorage.getItem('tour_cart')) || { data: [] };
        
        const totalItems = cart.data.length;
        const totalPrice = cart.data.reduce((sum, item) => sum + parseFloat(item.totalPrice), 0);
        
        setCartSummary({
            totalItems: totalItems,
            items: cart.data,
            totalPrice: totalPrice
        });
    };

    const handleClearCart = async () => {
        const result = await Swal.fire({
            title: 'Clear Cart',
            text: 'Are you sure you want to clear your cart?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, clear it!',
            cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
            localStorage.setItem('tour_cart', JSON.stringify({ data: [] }));
            setCartSummary({ 
                totalItems: 0, 
                items: [],
                totalPrice: 0
            });
            Swal.fire('Cleared!', 'Your cart has been cleared.', 'success');
        }
    };

    const handleProceedToCart = async () => {
        const userData = JSON.parse(localStorage.getItem('user_data'));

        if (!userData?.token) {
            Swal.fire({
                title: 'Please Login',
                text: 'You need to login to proceed to checkout',
                icon: 'warning',
                confirmButtonText: 'OK'
            });
            return;
        }

        if (cartSummary.totalItems === 0) {
            Swal.fire({
                title: 'Empty Cart',
                text: 'Your cart is empty',
                icon: 'warning',
                confirmButtonText: 'OK'
            });
            return;
        }

        // Show loading alert
        Swal.fire({
            title: 'Processing...',
            text: 'Adding tours to your cart',
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });

        try {
            // Her tour için işlem yap
            for (const tourItem of cartSummary.items) {
                // İlk ürünü ekle ve basket_item_id'yi al
                const initialResponse = await axios.post(
                    `${config.API_BASE_URL_V1}basket/add`,
                    {
                        offer_key: tourItem.offerKey,
                        type: "tour"
                    },
                    {
                        headers: {
                            'Authorization': `Bearer ${userData.token}`
                        }
                    }
                );

                if (initialResponse.data.status !== 'success') {
                    throw new Error(`Failed to add tour ${tourItem.tourName} to cart`);
                }

                // API yanıtından basket item ID'yi al
                const basketItem = initialResponse.data.data.basket.basketItems[0];
                if (!basketItem || !basketItem.id) {
                    throw new Error('Basket item ID not found in response');
                }

                const basketItemId = basketItem.id;

                // Eğer miktar 1'den fazlaysa, miktarı artır (şu an hep 1 ama ileride kullanım için)
                if (tourItem.quantity > 1) {
                    for (let i = 1; i < tourItem.quantity; i++) {
                        const plusResponse = await axios.post(
                            `${config.API_BASE_URL_V1}basket/plus-quantity/${basketItemId}`,
                            {},
                            {
                                headers: {
                                    'Authorization': `Bearer ${userData.token}`
                                }
                            }
                        );

                        if (plusResponse.data.status !== 'success') {
                            throw new Error(`Failed to increase quantity for tour ${tourItem.tourName}`);
                        }
                    }
                }
            }
            
            // Clear localStorage cart after successful API calls
            localStorage.removeItem('tour_cart');
            setCartSummary({
                totalItems: 0,
                items: [],
                totalPrice: 0
            });
            
            Swal.fire({
                toast: true,
                position: 'top-right',
                title: 'Success!',
                text: 'Your tours have been added to cart.',
                icon: 'success',
                showConfirmButton: false,
                timer: 2000
            });
            
            navigate('/cart');
        } catch (error) {
            console.error('Error processing cart:', error);
            Swal.fire({
                title: 'Error!',
                text: error.message || 'Failed to process cart',
                icon: 'error',
                confirmButtonText: 'OK'
            });
        }
        finally {
            Swal.close();
        }
    };

    // Initialize cart summary on component mount
    useEffect(() => {
        updateCartSummary();
        
        // Listen for cart changes
        const handleStorageChange = () => {
            updateCartSummary();
        };
        
        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    // Filter available dates when date range changes
    useEffect(() => {
        filterAvailableDates();
    }, [dateRangeStart, dateRangeEnd, tour?.availableDates]);

    if (loading) {
        return (
            <div id="loader-wrapper">
                <div id="loader">
                    <span className="loader-line"></span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <Layout>
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-md-6">
                            <div className="text-center py-5">
                                <h2>Error Loading Tour</h2>
                                <p className="text-muted">{error}</p>
                                <button 
                                    className="btn btn-primary"
                                    onClick={() => navigate('/tours')}
                                >
                                    Back to Tours
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </Layout>
        );
    }

    if (!tour) {
        return (
            <Layout>
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-md-6">
                            <div className="text-center py-5">
                                <h2>Tour Not Found</h2>
                                <p className="text-muted">The tour you're looking for doesn't exist.</p>
                                <button 
                                    className="btn btn-primary"
                                    onClick={() => navigate('/tours')}
                                >
                                    Back to Tours
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            {/* Custom Styles for Tabs and Gallery */}
            <style jsx>{`
                .nav-tabs-line {
                    border-bottom: 2px solid #e9ecef;
                }
                .nav-tabs-line .nav-link {
                    border: none;
                    border-bottom: 3px solid transparent;
                    background: none;
                    color: #6c757d;
                    font-weight: 500;
                    transition: all 0.3s ease;
                }
                .nav-tabs-line .nav-link:hover {
                    border-bottom-color: #007bff;
                    color: #007bff;
                    background: none;
                }
                .nav-tabs-line .nav-link.active {
                    border-bottom-color: #007bff;
                    color: #007bff;
                    background: none;
                }
                .timeline-item:last-child .timeline-marker::after {
                    display: none;
                }
                .timeline-marker {
                    position: relative;
                }
                .timeline-marker::after {
                    content: '';
                    position: absolute;
                    top: 100%;
                    left: 50%;
                    transform: translateX(-50%);
                    width: 2px;
                    height: 30px;
                    background-color: #dee2e6;
                }
                
                /* Infinite Gallery Section */
                .tour-gallery-section {
                    width: 100vw;
                    position: relative;
                    left: 50%;
                    right: 50%;
                    margin-left: -50vw;
                    margin-right: -50vw;
                    padding: 40px 0 60px 0;
                    background: #ffffff;
                }
                
                /* Infinite Gallery Carousel */
                .tour-gallery-slider.infinite-gallery {
                    width: 100%;
                    margin: 0;
                    padding: 0 20px;
                }
                
                .tour-gallery-slider.infinite-gallery .owl-stage-outer {
                    overflow: visible;
                }
                
                .tour-gallery-slider.infinite-gallery .owl-stage {
                    display: flex;
                    align-items: center;
                }
                
                /* Gallery Slide Items */
                .gallery-slide-item {
                    padding: 0 5px;
                    transition: all 0.3s ease;
                }
                
                .gallery-image-wrapper {
                    position: relative;
                    border-radius: 20px;
                    overflow: hidden;
                    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
                    transition: all 0.4s ease;
                    background: #fff;
                }
                
                .gallery-image-wrapper:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.25);
                }
                
                .gallery-image {
                    width: 100%;
                    height: 400px;
                    object-fit: cover;
                    transition: all 0.3s ease;
                    border-radius: 20px;
                }
                
                /* Navigation Buttons */
                .tour-gallery-slider .owl-nav {
                    position: absolute;
                    top: 50%;
                    width: 100%;
                    transform: translateY(-50%);
                    z-index: 15;
                    pointer-events: none;
                }
                
                .tour-gallery-slider .owl-nav button {
                    position: absolute;
                    width: 55px;
                    height: 55px;
                    background: rgba(255, 255, 255, 0.95) !important;
                    border: none;
                    border-radius: 50%;
                    color: #333 !important;
                    font-size: 18px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.3s ease;
                    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
                    pointer-events: all;
                    backdrop-filter: blur(10px);
                }
                
                .tour-gallery-slider .owl-nav button:hover {
                    background: #fff !important;
                    color: #007bff !important;
                    transform: scale(1.1);
                    box-shadow: 0 8px 25px rgba(0, 123, 255, 0.3);
                }
                
                .tour-gallery-slider .owl-nav .owl-prev {
                    left: 25px;
                }
                
                .tour-gallery-slider .owl-nav .owl-next {
                    right: 25px;
                }
                
                /* Center item styling */
                .tour-gallery-slider.infinite-gallery .owl-item.center .gallery-image-wrapper {
                    z-index: 10;
                }
                
                /* Smooth transition overlays */
                .tour-gallery-section::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    height: 40px;
                    background: linear-gradient(to bottom, rgba(255,255,255,0.3), transparent);
                    z-index: 1;
                    pointer-events: none;
                }
                
                /* Modern Booking Card Styles */
                .modern-booking-card {
                    background: linear-gradient(135deg, 
                        rgba(255, 240, 220, 0.8) 0%, 
                        rgba(255, 225, 195, 0.7) 50%,
                        rgba(255, 210, 170, 0.6) 100%);
                    border-radius: 10px;
                    box-shadow: 0 6px 15px rgba(255, 140, 70, 0.12);
                    overflow: hidden;
                    margin: 1rem 0;
                    border: 1px solid rgba(255, 180, 120, 0.5);
                    transition: all 0.3s ease;
                }

                .booking-header {
                    background: linear-gradient(135deg, 
                        rgba(255, 235, 210, 0.9) 0%, 
                        rgba(255, 220, 185, 0.8) 50%,
                        rgba(255, 205, 160, 0.7) 100%);
                    backdrop-filter: blur(3px);
                    border-bottom: 1px solid rgba(255, 180, 120, 0.4);
                    padding: 0.75rem 1rem;
                }

                .booking-header-content {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    color: rgba(220, 120, 60, 0.95);
                }

                .booking-icon {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 35px;
                    height: 35px;
                    background: linear-gradient(135deg, 
                        rgba(255, 140, 70, 0.25) 0%, 
                        rgba(240, 120, 50, 0.3) 100%);
                    border-radius: 50%;
                    font-size: 14px;
                    color: rgba(200, 100, 40, 0.85);
                    flex-shrink: 0;
                    transition: all 0.3s ease;
                }

                .booking-text {
                    display: flex;
                    flex-direction: column;
                }

                .booking-title {
                    font-size: 1.1rem;
                    font-weight: 600;
                    margin: 0;
                    color: rgba(210, 110, 50, 0.95);
                    transition: color 0.3s ease;
                }

                .booking-subtitle {
                    font-size: 0.8rem;
                    opacity: 0.75;
                    margin: 0;
                    color: rgba(190, 90, 30, 0.8);
                    transition: color 0.3s ease;
                }

                .booking-body {
                    background: rgba(255, 255, 255, 0.95);
                    padding: 1.25rem;
                }

                /* Selected Date Card */
                .selected-date-card {
                    background: linear-gradient(135deg, 
                        rgba(255, 235, 210, 0.85) 0%, 
                        rgba(255, 220, 185, 0.75) 50%,
                        rgba(255, 205, 160, 0.65) 100%);
                    border: 1px solid rgba(255, 180, 120, 0.6);
                    border-radius: 10px;
                    padding: 0.875rem;
                    margin-bottom: 1rem;
                    transition: all 0.3s ease;
                }

                .date-card-header {
                    display: flex;
                    align-items: center;
                    margin-bottom: 0.5rem;
                }

                .date-icon {
                    color: rgba(220, 120, 60, 0.85);
                    font-size: 1rem;
                    margin-right: 0.4rem;
                    transition: color 0.3s ease;
                }

                .date-title {
                    color: rgba(210, 110, 50, 0.95);
                    font-weight: 600;
                    margin: 0;
                    font-size: 0.95rem;
                    transition: color 0.3s ease;
                }

                .date-range {
                    font-size: 1.1rem;
                    font-weight: 700;
                    color: #2c3e50;
                    margin-bottom: 0;
                }

                .spots-info {
                    display: none;
                }

                .spots-label {
                    display: none;
                }

                .spots-count {
                    display: none;
                }

                /* Counter Sections */
                .counter-section {
                    margin-bottom: 1.25rem;
                }

                .counter-label {
                    display: block;
                    font-weight: 600;
                    color: #2c3e50;
                    margin-bottom: 0.5rem;
                    font-size: 1rem;
                }

                .counter-container {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    background: linear-gradient(135deg, 
                        rgba(255, 235, 210, 0.7) 0%, 
                        rgba(255, 220, 185, 0.6) 100%);
                    border: 1px solid rgba(255, 180, 120, 0.5);
                    border-radius: 8px;
                    padding: 0.625rem 0.875rem;
                    transition: all 0.3s ease;
                }

                .counter-container:hover {
                    border-color: rgba(220, 120, 60, 0.7);
                    box-shadow: 0 0 0 2px rgba(255, 140, 70, 0.15);
                    background: linear-gradient(135deg, 
                        rgba(255, 230, 200, 0.8) 0%, 
                        rgba(255, 215, 175, 0.7) 100%);
                }

                .counter-text {
                    font-weight: 500;
                    color: #475569;
                }

                .counter-controls {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                }

                .counter-btn {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 30px;
                    height: 30px;
                    border: 1px solid rgba(255, 180, 120, 0.7);
                    border-radius: 50%;
                    background: linear-gradient(135deg, 
                        rgba(255, 255, 255, 0.9) 0%, 
                        rgba(255, 245, 230, 0.8) 100%);
                    color: #64748b;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    font-size: 0.75rem;
                }

                .counter-btn:hover {
                    border-color: rgba(220, 120, 60, 0.8);
                    background: linear-gradient(135deg, 
                        rgba(240, 130, 70, 0.85) 0%, 
                        rgba(220, 110, 50, 0.9) 100%);
                    color: white;
                    transform: scale(1.05);
                }

                .counter-btn:active {
                    transform: scale(0.95);
                }

                .counter-btn-minus:hover {
                    border-color: #ef4444;
                    background: #ef4444;
                }

                .counter-value {
                    font-weight: 700;
                    color: #2c3e50;
                    font-size: 1rem;
                    min-width: 35px;
                    text-align: center;
                }

                /* Children Section */
                .children-section {
                    margin-bottom: 1.25rem;
                }

                .children-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 0.75rem;
                }

                .add-child-btn {
                    display: flex;
                    align-items: center;
                    gap: 0.3rem;
                    background: linear-gradient(135deg, 
                        rgba(240, 130, 70, 0.85) 0%, 
                        rgba(220, 110, 50, 0.8) 50%,
                        rgba(200, 90, 30, 0.75) 100%);
                    color: rgba(255, 255, 255, 0.95);
                    border: none;
                    border-radius: 16px;
                    padding: 0.35rem 0.7rem;
                    font-size: 0.8rem;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }

                .add-child-btn:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 4px 15px rgba(220, 120, 60, 0.3);
                    background: linear-gradient(135deg, 
                        rgba(250, 140, 80, 0.9) 0%, 
                        rgba(230, 120, 60, 0.85) 50%,
                        rgba(210, 100, 40, 0.8) 100%);
                }

                .children-list {
                    display: flex;
                    flex-direction: column;
                    gap: 0.5rem;
                }

                .child-item {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    background: rgba(255, 247, 240, 0.6);
                    border: 1px solid rgba(255, 212, 179, 0.4);
                    border-radius: 8px;
                    padding: 0.625rem 0.875rem;
                    transition: all 0.3s ease;
                }

                .child-item:hover {
                    border-color: rgba(255, 154, 86, 0.6);
                    box-shadow: 0 0 0 2px rgba(255, 154, 86, 0.1);
                }

                .child-label {
                    font-weight: 500;
                    color: #475569;
                }

                .child-controls {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }

                .age-select {
                    border: 2px solid #ffd4b3;
                    border-radius: 6px;
                    padding: 0.4rem;
                    font-size: 0.85rem;
                    background: white;
                    color: #475569;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .age-select:focus {
                    outline: none;
                    border-color: #ff9a56;
                    box-shadow: 0 0 0 3px rgba(255, 154, 86, 0.1);
                }

                .remove-child-btn {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 26px;
                    height: 26px;
                    border: 2px solid #ef4444;
                    border-radius: 50%;
                    background: white;
                    color: #ef4444;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .remove-child-btn:hover {
                    background: #ef4444;
                    color: white;
                    transform: scale(1.05);
                }

                .no-children {
                    text-align: center;
                    color: #64748b;
                    background: #fff7f0;
                    border: 2px dashed #ffd4b3;
                    border-radius: 10px;
                    padding: 1.25rem;
                    font-style: italic;
                    font-size: 0.9rem;
                }

                /* Price Summary */
                .price-loading {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 2rem;
                    background: #fff7f0;
                    border-radius: 12px;
                    margin-bottom: 1.25rem;
                }

                .loading-spinner {
                    width: 32px;
                    height: 32px;
                    border: 3px solid #ffd4b3;
                    border-top: 3px solid #ff9a56;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                    margin-bottom: 0.75rem;
                }

                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                .loading-text {
                    color: #64748b;
                    font-weight: 500;
                    font-size: 0.9rem;
                }

                .price-summary-card {
                    background: linear-gradient(135deg, 
                        rgba(255, 235, 210, 0.8) 0%, 
                        rgba(255, 220, 185, 0.7) 50%,
                        rgba(255, 205, 160, 0.6) 100%);
                    border: 1px solid rgba(255, 180, 120, 0.5);
                    border-radius: 10px;
                    padding: 1rem;
                    margin-bottom: 1rem;
                    transition: all 0.3s ease;
                }

                .price-title {
                    color: rgba(210, 110, 50, 0.95);
                    font-weight: 700;
                    margin-bottom: 0.875rem;
                    font-size: 1.05rem;
                    transition: color 0.3s ease;
                }

                .total-price {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 1rem;
                    padding-bottom: 0.75rem;
                    border-bottom: 2px solid #ffd4b3;
                }

                .price-label {
                    font-weight: 600;
                    color: #475569;
                    font-size: 1rem;
                }

                .price-amount {
                    font-size: 1.4rem;
                    font-weight: 800;
                    color: rgba(210, 110, 50, 0.95);
                    transition: color 0.3s ease;
                }

                .price-breakdown {
                    display: flex;
                    flex-direction: column;
                    gap: 0.4rem;
                }

                .price-item {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    color: #64748b;
                    font-size: 0.85rem;
                }

                .price-placeholder {
                    background: #fff7f0;
                    border: 2px dashed #ffd4b3;
                    border-radius: 12px;
                    padding: 2rem;
                    text-align: center;
                    color: #64748b;
                    font-style: italic;
                    margin-bottom: 1.25rem;
                    font-size: 0.9rem;
                }

                /* Booking Actions */
                .booking-actions {
                    display: flex;
                    flex-direction: column;
                    gap: 0;
                }

                .book-now-btn {
                    display: none;
                }

                .add-cart-btn {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.4rem;
                    background: linear-gradient(135deg, 
                        rgba(255, 255, 255, 0.95) 0%, 
                        rgba(255, 245, 230, 0.9) 100%);
                    color: rgba(210, 110, 50, 0.9);
                    border: 1px solid rgba(220, 120, 60, 0.7);
                    border-radius: 8px;
                    padding: 0.625rem 1.25rem;
                    font-size: 0.9rem;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }

                .add-cart-btn:hover:not(:disabled) {
                    background: linear-gradient(135deg, 
                        rgba(240, 130, 70, 0.85) 0%, 
                        rgba(220, 110, 50, 0.9) 100%);
                    color: rgba(255, 255, 255, 0.95);
                    transform: translateY(-1px);
                    box-shadow: 0 6px 15px rgba(220, 120, 60, 0.3);
                }

                /* Available Dates - Selected Date Card Override */
                .border-primary.bg-primary.bg-opacity-10 {
                    background: linear-gradient(135deg, 
                        rgba(255, 235, 210, 0.85) 0%, 
                        rgba(255, 220, 185, 0.75) 50%,
                        rgba(255, 205, 160, 0.65) 100%) !important;
                    border-color: rgba(220, 120, 60, 0.7) !important;
                    transition: all 0.3s ease;
                }

                .border-primary.bg-primary.bg-opacity-10:hover {
                    background: linear-gradient(135deg, 
                        rgba(255, 230, 200, 0.9) 0%, 
                        rgba(255, 215, 175, 0.8) 50%,
                        rgba(255, 200, 150, 0.7) 100%) !important;
                    border-color: rgba(210, 110, 50, 0.8) !important;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 15px rgba(220, 120, 60, 0.2);
                }

                .bg-primary.badge {
                    background: linear-gradient(135deg, 
                        rgba(240, 130, 70, 0.9) 0%, 
                        rgba(220, 110, 50, 0.95) 100%) !important;
                    color: rgba(255, 255, 255, 0.95) !important;
                    font-weight: 600;
                }

                .text-primary.fw-semibold {
                    color: rgba(210, 110, 50, 0.95) !important;
                }

                .add-cart-btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .cart-icon {
                    font-size: 1rem;
                }

                .booking-warning {
                    background: linear-gradient(135deg, #fef3cd 0%, #fde68a 100%);
                    border: 2px solid #f59e0b;
                    border-radius: 10px;
                    padding: 0.75rem;
                    color: #92400e;
                    font-size: 0.85rem;
                    font-weight: 500;
                    margin-top: 0.75rem;
                    text-align: center;
                }

                @media (max-width: 768px) {
                    .booking-header {
                        padding: 1rem;
                    }
                    
                    .booking-body {
                        padding: 1.25rem;
                    }
                    
                    .booking-title {
                        font-size: 1.3rem;
                    }
                    
                    .price-amount {
                        font-size: 1.3rem;
                    }
                    
                    .counter-container, .child-item {
                        padding: 0.625rem 0.875rem;
                    }
                    
                    .selected-date-card {
                        padding: 0.875rem;
                    }
                    
                    .price-summary-card {
                        padding: 1rem;
                    }
                }
                
                .tour-gallery-section::after {
                    content: '';
                    position: absolute;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    height: 40px;
                    background: linear-gradient(to top, rgba(255,255,255,0.3), transparent);
                    z-index: 1;
                    pointer-events: none;
                }
                
                /* Mobile Responsive */
                @media (max-width: 768px) {
                    .tour-gallery-section {
                        padding: 30px 0 45px 0;
                    }
                    
                    .tour-gallery-slider.infinite-gallery {
                        padding: 0 10px;
                    }
                    
                    .gallery-image {
                        height: 300px;
                    }
                    
                    .gallery-image-wrapper {
                        border-radius: 15px;
                    }
                    
                    .tour-gallery-slider .owl-nav button {
                        width: 45px;
                        height: 45px;
                        font-size: 16px;
                    }
                    
                    .tour-gallery-slider .owl-nav .owl-prev {
                        left: 15px;
                    }
                    
                    .tour-gallery-slider .owl-nav .owl-next {
                        right: 15px;
                    }
                }
                
                @media (max-width: 480px) {
                    .gallery-image {
                        height: 250px;
                    }
                    
                    .gallery-image-wrapper {
                        border-radius: 12px;
                    }
                    
                    .tour-gallery-slider .owl-nav button {
                        width: 40px;
                        height: 40px;
                        font-size: 14px;
                    }
                    
                    .tour-gallery-slider .owl-nav .owl-prev {
                        left: 10px;
                    }
                    
                    .tour-gallery-slider .owl-nav .owl-next {
                        right: 10px;
                    }
                }

                /* Fixed Cart Footer Styles */
                .fixed-cart-footer {
                    position: fixed;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    background: linear-gradient(135deg, 
                        rgba(255, 255, 255, 0.98) 0%, 
                        rgba(248, 249, 250, 0.95) 100%);
                    backdrop-filter: blur(20px);
                    padding: 15px 20px;
                    box-shadow: 0 -8px 32px rgba(0, 0, 0, 0.15);
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    z-index: 1000;
                    border-top: 1px solid rgba(255, 180, 120, 0.3);
                    transition: all 0.3s ease;
                }

                .fixed-cart-footer:hover {
                    box-shadow: 0 -12px 40px rgba(0, 0, 0, 0.2);
                }

                .cart-summary {
                    display: flex;
                    align-items: center;
                    gap: 15px;
                }

                .cart-summary .cart-icon {
                    font-size: 28px;
                    color: rgba(220, 120, 60, 0.9);
                    padding: 12px;
                    background: linear-gradient(135deg, 
                        rgba(255, 235, 210, 0.8) 0%, 
                        rgba(255, 220, 185, 0.6) 100%);
                    border-radius: 50%;
                    border: 2px solid rgba(255, 180, 120, 0.4);
                    transition: all 0.3s ease;
                }

                .cart-summary .cart-icon:hover {
                    transform: scale(1.1);
                    color: rgba(210, 110, 50, 1);
                }

                .summary-text {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                }

                .total-items {
                    font-weight: 700;
                    font-size: 16px;
                    color: rgba(210, 110, 50, 0.95);
                    letter-spacing: 0.3px;
                }

                .tour-items {
                    display: flex;
                    gap: 8px;
                    flex-wrap: wrap;
                    max-width: 400px;
                }

                .tour-item {
                    font-size: 13px;
                    color: #64748b;
                    background: linear-gradient(135deg, 
                        rgba(255, 240, 220, 0.7) 0%, 
                        rgba(255, 230, 200, 0.5) 100%);
                    padding: 4px 10px;
                    border-radius: 12px;
                    border: 1px solid rgba(255, 200, 150, 0.4);
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    max-width: 200px;
                    transition: all 0.2s ease;
                }

                .tour-item:hover {
                    background: linear-gradient(135deg, 
                        rgba(255, 235, 210, 0.8) 0%, 
                        rgba(255, 220, 185, 0.6) 100%);
                }

                .more-items {
                    font-size: 13px;
                    color: rgba(220, 120, 60, 0.8);
                    background: linear-gradient(135deg, 
                        rgba(255, 220, 185, 0.6) 0%, 
                        rgba(255, 205, 160, 0.4) 100%);
                    padding: 4px 10px;
                    border-radius: 12px;
                    border: 1px solid rgba(255, 180, 120, 0.5);
                    font-weight: 600;
                }

                .total-price {
                    font-weight: 800;
                    font-size: 18px;
                    color: rgba(210, 110, 50, 0.95);
                    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
                    margin-top: 2px;
                }

                .cart-actions {
                    display: flex;
                    gap: 12px;
                    align-items: center;
                }

                .cart-actions .btn {
                    padding: 10px 20px;
                    font-weight: 600;
                    border-radius: 10px;
                    transition: all 0.3s ease;
                    font-size: 14px;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    min-width: 120px;
                    justify-content: center;
                }

                .cart-actions .btn-outline-danger {
                    border: 2px solid #ef4444;
                    color: #ef4444;
                    background: rgba(255, 255, 255, 0.9);
                }

                .cart-actions .btn-outline-danger:hover {
                    background: #ef4444;
                    color: white;
                    transform: translateY(-2px);
                    box-shadow: 0 6px 20px rgba(239, 68, 68, 0.3);
                }

                .cart-actions .btn-primary {
                    background: linear-gradient(135deg, 
                        rgba(240, 130, 70, 0.9) 0%, 
                        rgba(220, 110, 50, 0.95) 100%);
                    border: none;
                    color: white;
                    box-shadow: 0 4px 15px rgba(220, 120, 60, 0.3);
                }

                .cart-actions .btn-primary:hover {
                    background: linear-gradient(135deg, 
                        rgba(250, 140, 80, 1) 0%, 
                        rgba(230, 120, 60, 1) 100%);
                    transform: translateY(-2px);
                    box-shadow: 0 8px 25px rgba(220, 120, 60, 0.4);
                }

                /* Mobile Responsive */
                @media (max-width: 768px) {
                    .fixed-cart-footer {
                        flex-direction: column;
                        gap: 15px;
                        padding: 12px 15px;
                    }

                    .cart-summary {
                        width: 100%;
                        justify-content: center;
                    }

                    .summary-text {
                        text-align: center;
                        align-items: center;
                    }

                    .tour-items {
                        justify-content: center;
                        max-width: 100%;
                    }

                    .tour-item {
                        max-width: 150px;
                        font-size: 12px;
                    }

                    .cart-actions {
                        width: 100%;
                        justify-content: space-between;
                    }

                    .cart-actions .btn {
                        flex: 1;
                        min-width: auto;
                        padding: 12px 16px;
                    }
                }

                @media (max-width: 480px) {
                    .cart-summary .cart-icon {
                        font-size: 24px;
                        padding: 10px;
                    }

                    .total-items {
                        font-size: 14px;
                    }

                    .total-price {
                        font-size: 16px;
                    }

                    .tour-item {
                        font-size: 11px;
                        padding: 3px 8px;
                        max-width: 120px;
                    }
                }
            `}</style>
            
            {/* Breadcrumb Section */}
            <div className="breadcrumb-bar breadcrumb-bg-01 text-center">
                <div className="container">
                    <div className="row">
                        <div className="col-md-12 col-12">
                            <h2 className="breadcrumb-title mb-2">{tour?.name || 'Tour Detail'}</h2>
                            <nav aria-label="breadcrumb">
                                <ol className="breadcrumb justify-content-center mb-0">
                                    <li className="breadcrumb-item">
                                        <a href="/"><i className="isax isax-home5"></i></a>
                                    </li>
                                    <li className="breadcrumb-item">
                                        <a href="/tours">Tours</a>
                                    </li>
                                    <li className="breadcrumb-item active" aria-current="page">
                                        {tour?.area?.name || tour?.location || 'Tour Detail'}
                                    </li>
                                </ol>
                            </nav>
                        </div>
                    </div>
                </div>
            </div>

            {/* Infinite Gallery Slider */}
            <div className="tour-gallery-section mb-5">
                <div className="position-relative">
                    {tour?.images && tour.images.length > 0 ? (
                        <OwlCarousel 
                            className="tour-gallery-slider infinite-gallery"
                            loop={true}
                            autoplay={true}
                            autoplayTimeout={4000}
                            autoplayHoverPause={true}
                            nav={true}
                            navText={[
                                '<i class="fas fa-chevron-left"></i>',
                                '<i class="fas fa-chevron-right"></i>'
                            ]}
                            dots={false}
                            smartSpeed={800}
                            margin={10}
                            stagePadding={0}
                            center={true}
                            responsive={{
                                0: { 
                                    items: 1,
                                    stagePadding: 50
                                },
                                576: { 
                                    items: 1.5,
                                    stagePadding: 80
                                },
                                768: { 
                                    items: 2,
                                    stagePadding: 100
                                },
                                992: { 
                                    items: 2.5,
                                    stagePadding: 150
                                },
                                1200: { 
                                    items: 3,
                                    stagePadding: 200
                                },
                                1400: { 
                                    items: 3.5,
                                    stagePadding: 250
                                }
                            }}
                        >
                            {/* Duplicate images for infinite effect */}
                            {[...tour.images, ...tour.images, ...tour.images].map((image, index) => (
                                <div key={`${image.id}-${index}`} className="gallery-slide-item">
                                    <div className="gallery-image-wrapper">
                                        <img 
                                            src={image.path} 
                                            alt={`${tour.name} - Image ${(index % tour.images.length) + 1}`}
                                            className="gallery-image"
                                            onError={(e) => {
                                                e.target.src = "/assets/img/tours/default-tour.jpg";
                                            }}
                                        />

                                    </div>
                                </div>
                            ))}
                        </OwlCarousel>
                    ) : (
                        <div className="gallery-slide-item">
                            <div className="gallery-image-wrapper">
                                <img 
                                    src="/assets/img/tours/default-tour.jpg" 
                                    alt={tour?.name || "Tour"}
                                    className="gallery-image"
                                />
                            </div>
                        </div>
                    )}
                    

                </div>
            </div>

            {/* Tour Content */}
            <div className="content py-5">
                <div className="container">
                    <div className="row justify-content-center">
                        {/* Main Column - Tour Info */}
                        <div className="col-lg-10">

                            {/* Tour Header */}
                            <div className="mb-4">
                                <h1 className="display-6 fw-bold mb-3">{tour?.name || 'Tour Name'}</h1>
                                <div className="d-flex flex-wrap align-items-center gap-4 mb-3">
                                    <div className="d-flex align-items-center">
                                        <FaStar className="text-warning me-1" />
                                        <span className="fw-semibold">{tour?.rating || 4.8}</span>
                                        <span className="text-muted ms-1">({tour?.reviews || 0} reviews)</span>
                                    </div>
                                    <div className="d-flex align-items-center text-muted">
                                        <FaMapMarkerAlt className="me-1" />
                                        <span>{tour?.area?.name || tour?.location || 'Location'}</span>
                                    </div>
                                    <div className="d-flex align-items-center text-muted">
                                        <FaUsers className="me-1" />
                                        <span>{tour?.bookingCount || '0'}+ booked</span>
                                    </div>
                                </div>
                            </div>

                            {/* Tour Info Cards */}
                            <div className="row g-3 mb-5">
                                <div className="col-md-3 col-6">
                                    <div className="card h-100 border-0 shadow-sm">
                                        <div className="card-body text-center">
                                            <div className="bg-primary bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{width: '60px', height: '60px'}}>
                                                <FaClock className="text-primary fs-4" />
                                            </div>
                                            <h6 className="card-title mb-1">Duration</h6>
                                            <p className="card-text text-muted small">{tour?.duration || 'N/A'}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3 col-6">
                                    <div className="card h-100 border-0 shadow-sm">
                                        <div className="card-body text-center">
                                            <div className="bg-success bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{width: '60px', height: '60px'}}>
                                                <FaUsers className="text-success fs-4" />
                                            </div>
                                            <h6 className="card-title mb-1">Group Size</h6>
                                            <p className="card-text text-muted small">{tour?.groupSize || 'N/A'}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3 col-6">
                                    <div className="card h-100 border-0 shadow-sm">
                                        <div className="card-body text-center">
                                            <div className="bg-info bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{width: '60px', height: '60px'}}>
                                                <FaCalendarAlt className="text-info fs-4" />
                                            </div>
                                            <h6 className="card-title mb-1">Languages</h6>
                                            <p className="card-text text-muted small">{tour?.languages || 'English'}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3 col-6">
                                    <div className="card h-100 border-0 shadow-sm">
                                        <div className="card-body text-center">
                                            <div className="bg-warning bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{width: '60px', height: '60px'}}>
                                                <FaUsers className="text-warning fs-4" />
                                            </div>
                                            <h6 className="card-title mb-1">Ages</h6>
                                            <p className="card-text text-muted small">{tour?.ageRange || '18-99 years'}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Tour Information Tabs */}
                            <div className="card border-0 shadow-sm mb-4">
                                <div className="card-header bg-white border-bottom-0 p-0">
                                    <ul className="nav nav-tabs nav-tabs-line" id="tourTabs" role="tablist">
                                        <li className="nav-item" role="presentation">
                                            <button 
                                                className={`nav-link px-4 py-3 ${activeTab === 'about' ? 'active' : ''}`}
                                                type="button" 
                                                role="tab" 
                                                onClick={() => setActiveTab('about')}
                                            >
                                                About Tour
                                            </button>
                                        </li>

                                        <li className="nav-item" role="presentation">
                                            <button 
                                                className={`nav-link px-4 py-3 ${activeTab === 'highlights' ? 'active' : ''}`}
                                                type="button" 
                                                role="tab" 
                                                onClick={() => setActiveTab('highlights')}
                                            >
                                                Highlights
                                            </button>
                                        </li>
                                        <li className="nav-item" role="presentation">
                                            <button 
                                                className={`nav-link px-4 py-3 ${activeTab === 'itinerary' ? 'active' : ''}`}
                                                type="button" 
                                                role="tab" 
                                                onClick={() => setActiveTab('itinerary')}
                                            >
                                                Itinerary
                                            </button>
                                        </li>
                                        <li className="nav-item" role="presentation">
                                            <button 
                                                className={`nav-link px-4 py-3 ${activeTab === 'included' ? 'active' : ''}`}
                                                type="button" 
                                                role="tab" 
                                                onClick={() => setActiveTab('included')}
                                            >
                                                Included/Excluded
                                            </button>
                                        </li>
                                    </ul>
                                </div>
                                
                                <div className="card-body p-4">
                                    <div className="tab-content" id="tourTabsContent">
                                        {/* About Tab */}
                                        {activeTab === 'about' && (
                                            <div className="tab-pane-content">
                                                <h4 className="mb-3">About this tour</h4>
                                                <div className="text-muted" dangerouslySetInnerHTML={{ __html: tour?.description || 'Experience an amazing tour that will create lasting memories. This tour offers unique insights into local culture and history.' }} />
                                            </div>
                                        )}



                                        {/* Highlights Tab */}
                                        {activeTab === 'highlights' && (
                                            <div className="tab-pane-content">
                                                <h4 className="mb-4">Tour Highlights</h4>
                                                <div className="row g-3">
                                                    {tour?.highlights && Array.isArray(tour.highlights) && tour.highlights.length > 0 ? (
                                                        tour.highlights.map((highlight, index) => (
                                                            <div key={highlight.id || index} className="col-md-6">
                                                                <div className="d-flex align-items-start">
                                                                    <div className="bg-success bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0" style={{width: '32px', height: '32px'}}>
                                                                        <FaCheck className="text-success" style={{fontSize: '14px'}} />
                                                                    </div>
                                                                    <span className="text-muted">
                                                                        {typeof highlight === 'string' ? highlight : highlight.title || highlight.name || 'Tour highlight'}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <>
                                                            <div className="col-md-6">
                                                                <div className="d-flex align-items-start">
                                                                    <div className="bg-success bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0" style={{width: '32px', height: '32px'}}>
                                                                        <FaCheck className="text-success" style={{fontSize: '14px'}} />
                                                                    </div>
                                                                    <span className="text-muted">Professional tour guide</span>
                                                                </div>
                                                            </div>
                                                            <div className="col-md-6">
                                                                <div className="d-flex align-items-start">
                                                                    <div className="bg-success bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0" style={{width: '32px', height: '32px'}}>
                                                                        <FaCheck className="text-success" style={{fontSize: '14px'}} />
                                                                    </div>
                                                                    <span className="text-muted">All entrance fees included</span>
                                                                </div>
                                                            </div>
                                                            <div className="col-md-6">
                                                                <div className="d-flex align-items-start">
                                                                    <div className="bg-success bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0" style={{width: '32px', height: '32px'}}>
                                                                        <FaCheck className="text-success" style={{fontSize: '14px'}} />
                                                                    </div>
                                                                    <span className="text-muted">Transportation included</span>
                                                                </div>
                                                            </div>
                                                            <div className="col-md-6">
                                                                <div className="d-flex align-items-start">
                                                                    <div className="bg-success bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0" style={{width: '32px', height: '32px'}}>
                                                                        <FaCheck className="text-success" style={{fontSize: '14px'}} />
                                                                    </div>
                                                                    <span className="text-muted">Free cancellation</span>
                                                                </div>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Itinerary Tab */}
                                        {activeTab === 'itinerary' && (
                                            <div className="tab-pane-content">
                                                <h4 className="mb-4">Tour Itinerary</h4>
                                                {tour?.plans && tour.plans.length > 0 ? (
                                                    <div className="timeline">
                                                        {tour.plans.map((plan, index) => (
                                                            <div key={plan.id} className="timeline-item d-flex mb-4">
                                                                <div className="timeline-marker bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0" style={{width: '40px', height: '40px'}}>
                                                                    <span className="fw-bold">{index + 1}</span>
                                                                </div>
                                                                <div className="timeline-content">
                                                                    <h5 className="mb-2">{plan.title}</h5>
                                                                    <p className="text-muted mb-0">{plan.description}</p>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <p className="text-muted">Detailed itinerary will be provided upon booking.</p>
                                                )}
                                            </div>
                                        )}

                                        {/* Included/Excluded Tab */}
                                        {activeTab === 'included' && (
                                            <div className="tab-pane-content">
                                                {/* What's Included */}
                                                {tour?.inclusions && tour.inclusions.length > 0 && (
                                                    <div className="mb-5">
                                                        <h4 className="mb-4 text-success">
                                                            <FaCheck className="me-2" />
                                                            What's Included
                                                        </h4>
                                                        <div className="row g-3">
                                                            {tour.inclusions.map((inclusion) => (
                                                                <div key={inclusion.id} className="col-md-6">
                                                                    <div className="d-flex align-items-start">
                                                                        <div className="bg-success bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0" style={{width: '32px', height: '32px'}}>
                                                                            <FaCheck className="text-success" style={{fontSize: '14px'}} />
                                                                        </div>
                                                                        <span className="text-muted">{inclusion.title}</span>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* What's Not Included */}
                                                {tour?.exclusions && tour.exclusions.length > 0 && (
                                                    <div>
                                                        <h4 className="mb-4 text-danger">
                                                            <FaMinus className="me-2" />
                                                            What's Not Included
                                                        </h4>
                                                        <div className="row g-3">
                                                            {tour.exclusions.map((exclusion) => (
                                                                <div key={exclusion.id} className="col-md-6">
                                                                    <div className="d-flex align-items-start">
                                                                        <div className="bg-danger bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0" style={{width: '32px', height: '32px'}}>
                                                                            <FaMinus className="text-danger" style={{fontSize: '14px'}} />
                                                                        </div>
                                                                        <span className="text-muted">{exclusion.title}</span>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                {(!tour?.inclusions || tour.inclusions.length === 0) && (!tour?.exclusions || tour.exclusions.length === 0) && (
                                                    <p className="text-muted">Inclusion and exclusion details will be provided upon booking.</p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Available Dates */}
                            {tour?.availableDates && tour.availableDates.length > 0 && (
                                <div className="card border-0 shadow-sm mb-4">
                                    <div className="card-body p-4">
                                        <div className="d-flex justify-content-between align-items-center mb-4">
                                            <h3 className="h4 mb-0">Available Dates</h3>
                                            <span className="badge bg-primary fs-6">
                                                {filteredAvailableDates.length} of {tour.availableDates.length} dates
                                            </span>
                                        </div>
                                        
                                        {/* Date Range Filter */}
                                        <div className="card bg-light border-0 mb-4">
                                            <div className="card-body p-3">
                                                <h6 className="card-title mb-3">
                                                    <i className="fas fa-filter me-2 text-primary"></i>
                                                    Filter by Date Range
                                                </h6>
                                                <div className="row g-3">
                                                    <div className="col-md-6">
                                                        <label className="form-label small fw-medium">From Date</label>
                                                        <input
                                                            type="date"
                                                            className="form-control"
                                                            value={dateRangeStart}
                                                            min={new Date().toISOString().split('T')[0]}
                                                            onChange={(e) => setDateRangeStart(e.target.value)}
                                                            style={{
                                                                borderRadius: '8px',
                                                                border: '1px solid #dee2e6'
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label small fw-medium">To Date</label>
                                                        <input
                                                            type="date"
                                                            className="form-control"
                                                            value={dateRangeEnd}
                                                            min={dateRangeStart || new Date().toISOString().split('T')[0]}
                                                            onChange={(e) => setDateRangeEnd(e.target.value)}
                                                            style={{
                                                                borderRadius: '8px',
                                                                border: '1px solid #dee2e6'
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="mt-2">
                                                    <small className="text-muted">
                                                        <i className="fas fa-info-circle me-1"></i>
                                                        Showing tours available between selected date range
                                                    </small>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Filtered Available Dates */}
                                        {filteredAvailableDates.length > 0 ? (
                                            <div className="row g-3">
                                                {filteredAvailableDates.map((dateOption, index) => (
                                                    <div key={index} className="col-md-6 col-lg-4">
                                                        <div 
                                                            className={`card border h-100 cursor-pointer ${
                                                                selectedDateRange === dateOption.date 
                                                                    ? 'border-primary bg-primary bg-opacity-10' 
                                                                    : dateOption.is_available 
                                                                        ? 'border-success' 
                                                                        : 'border-danger'
                                                            }`}
                                                            style={{ cursor: dateOption.is_available ? 'pointer' : 'not-allowed' }}
                                                            onClick={() => dateOption.is_available && handleDateSelection(dateOption.date)}
                                                        >
                                                            <div className="card-body p-3">
                                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                                    <span className="fw-semibold">{formatDateRange(dateOption.date)}</span>
                                                                    <span className={`badge ${
                                                                        selectedDateRange === dateOption.date 
                                                                            ? 'bg-primary' 
                                                                            : dateOption.is_available 
                                                                                ? 'bg-success' 
                                                                                : 'bg-danger'
                                                                    }`}>
                                                                        {selectedDateRange === dateOption.date 
                                                                            ? 'Selected' 
                                                                            : dateOption.is_available 
                                                                                ? 'Available' 
                                                                                : 'Full'}
                                                                    </span>
                                                                </div>
                                                                {selectedDateRange === dateOption.date && (
                                                                    <div className="mt-2">
                                                                        <small className="text-primary fw-semibold">
                                                                            ✓ Selected
                                                                        </small>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="text-center py-5">
                                                <div className="mb-3">
                                                    <i className="fas fa-calendar-times text-muted" style={{ fontSize: '3rem' }}></i>
                                                </div>
                                                <h5 className="text-muted mb-2">No Available Dates Found</h5>
                                                <p className="text-muted">
                                                    No tours are available in the selected date range. 
                                                    Try adjusting your date range to see more options.
                                                </p>
                                                <button 
                                                    className="btn btn-outline-primary btn-sm"
                                                    onClick={() => {
                                                        const today = new Date();
                                                        const start = new Date(today);
                                                        start.setDate(today.getDate() + 1);
                                                        const end = new Date(start);
                                                        end.setDate(start.getDate() + 30);
                                                        
                                                        setDateRangeStart(start.toISOString().split('T')[0]);
                                                        setDateRangeEnd(end.toISOString().split('T')[0]);
                                                    }}
                                                >
                                                    Reset Date Range
                                                </button>
                                            </div>
                                        )}

                                        {/* Book Your Tour Section - Inside Available Dates */}
                                        {selectedDateRange && (
                                            <div className="mt-4 pt-4 border-top">
                                                <div className="modern-booking-card">
                                                    <div className="booking-header">
                                                        <div className="booking-header-content">
                                                            <div className="booking-icon">
                                                                <FaCalendarCheck />
                                                            </div>
                                                            <div className="booking-text">
                                                                <h4 className="booking-title">Book Your Tour</h4>
                                                                <p className="booking-subtitle">Complete your reservation</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="booking-body">
                                                        <div className="row g-4">
                                                            {/* Selected Date Info */}
                                                            <div className="col-md-6">
                                                                <div className="selected-date-card">
                                                                    <div className="date-card-header">
                                                                        <FaCalendarAlt className="date-icon" />
                                                                        <h6 className="date-title">Selected Tour Dates</h6>
                                                                    </div>
                                                                    <div className="date-range">
                                                                        {formatDateRange(selectedDateRange)}
                                                                    </div>
                                                                </div>

                                                                {/* Adults Counter */}
                                                                <div className="counter-section">
                                                                    <label className="counter-label">Adults (18+)</label>
                                                                    <div className="counter-container">
                                                                        <span className="counter-text">Adults</span>
                                                                        <div className="counter-controls">
                                                                            <button
                                                                                type="button"
                                                                                className="counter-btn counter-btn-minus"
                                                                                onClick={() => setAdults(Math.max(1, adults - 1))}
                                                                            >
                                                                                <FaMinus size={10} />
                                                                            </button>
                                                                            <span className="counter-value">{adults}</span>
                                                                            <button
                                                                                type="button"
                                                                                className="counter-btn counter-btn-plus"
                                                                                onClick={() => setAdults(adults + 1)}
                                                                            >
                                                                                <FaPlus size={10} />
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {/* Children */}
                                                                <div className="children-section">
                                                                    <div className="children-header">
                                                                        <label className="counter-label">Children</label>
                                                                        <button
                                                                            type="button"
                                                                            className="add-child-btn"
                                                                            onClick={addChild}
                                                                        >
                                                                            <FaPlus size={10} />
                                                                            Add Child
                                                                        </button>
                                                                    </div>
                                                                    {children.length > 0 ? (
                                                                        <div className="children-list">
                                                                            {children.map((child, index) => (
                                                                                <div key={index} className="child-item">
                                                                                    <span className="child-label">Child {index + 1}</span>
                                                                                    <div className="child-controls">
                                                                                        <select
                                                                                            className="age-select"
                                                                                            value={child.age}
                                                                                            onChange={(e) => updateChildAge(index, e.target.value)}
                                                                                        >
                                                                                            {Array.from({length: 18}, (_, i) => (
                                                                                                <option key={i} value={i}>{i} years</option>
                                                                                            ))}
                                                                                        </select>
                                                                                        <button
                                                                                            type="button"
                                                                                            className="remove-child-btn"
                                                                                            onClick={() => removeChild(index)}
                                                                                        >
                                                                                            <FaMinus size={8} />
                                                                                        </button>
                                                                                    </div>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    ) : (
                                                                        <div className="no-children">
                                                                            No children added
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {/* Price Summary */}
                                                            <div className="col-md-6">
                                                                {priceLoading ? (
                                                                    <div className="price-loading">
                                                                        <div className="loading-spinner"></div>
                                                                        <div className="loading-text">Calculating price...</div>
                                                                    </div>
                                                                ) : priceData?.quick_summary ? (
                                                                    <div className="price-summary-card">
                                                                        <h5 className="price-title">Price Summary</h5>
                                                                        <div className="total-price">
                                                                            <span className="price-label">Total Price</span>
                                                                            <span className="price-amount">
                                                                                {priceData.quick_summary.grand_total} {tour?.currency?.symbol || '€'}
                                                                            </span>
                                                                        </div>
                                                                        <div className="price-breakdown">
                                                                            <div className="price-item">
                                                                                <span>Per Person Avg:</span>
                                                                                <span>{priceData.quick_summary.per_person_avg} {tour?.currency?.symbol || '€'}</span>
                                                                            </div>
                                                                            <div className="price-item">
                                                                                <span>Adults ({adults}):</span>
                                                                                <span>{adults} × Price</span>
                                                                            </div>
                                                                            {children.length > 0 && (
                                                                                <div className="price-item">
                                                                                    <span>Children ({children.length}):</span>
                                                                                    <span>{children.length} × Child Price</span>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <div className="price-placeholder">
                                                                        Price will be calculated automatically
                                                                    </div>
                                                                )}

                                                                {/* Action Buttons */}
                                                                <div className="booking-actions">
                                                                    <button 
                                                                        className="add-cart-btn"
                                                                        disabled={!priceData?.quick_summary?.can_book || priceLoading}
                                                                        onClick={addToCart}
                                                                    >
                                                                        <FaShoppingCart className="cart-icon" />
                                                                        Add to Cart
                                                                    </button>
                                                                </div>

                                                                {!priceData?.quick_summary?.can_book && priceData && (
                                                                    <div className="booking-warning">
                                                                        Selected dates are not available for booking. Please choose different dates.
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Fixed Cart Footer */}
            {cartSummary.totalItems > 0 && (
                <div className="fixed-cart-footer">
                    <div className="cart-summary">
                        <FaShoppingCart className="cart-icon" />
                        <div className="summary-text">
                            <span className="total-items">{cartSummary.totalItems} tour{cartSummary.totalItems > 1 ? 's' : ''} selected</span>
                            <div className="tour-items">
                                {cartSummary.items.slice(0, 2).map((item, index) => (
                                    <span key={index} className="tour-item">
                                        {item.tourName} - {item.adults} adults {item.children.length > 0 ? `+ ${item.children.length} children` : ''}
                                    </span>
                                ))}
                                {cartSummary.items.length > 2 && (
                                    <span className="more-items">+{cartSummary.items.length - 2} more</span>
                                )}
                            </div>
                            <div className="total-price">
                                Total: {cartSummary.totalPrice.toFixed(2)} {cartSummary.items[0]?.currency || '€'}
                            </div>
                        </div>
                    </div>
                    <div className="cart-actions">
                        <button className="btn btn-outline-danger" onClick={handleClearCart}>
                            <FaTrash /> Clear Cart
                        </button>
                        <button className="btn btn-primary" onClick={handleProceedToCart}>
                            Confirm Cart
                        </button>
                    </div>
                </div>
            )}
        </Layout>
    );
} 